import { type PrimitiveAtom, atom, useAtom } from "jotai";
import { useCallback, useRef } from "react";
import type { UID } from "#scripts/vmc/WorldState.ts";
import { World } from "#scripts/vmc/WorldState.ts";
import { useWorldState } from "#scripts/world-state-demo/world-state-context.ts";
import { DevEntityDetails } from "./DevEntityDetails.tsx";

export interface TrackedEntity {
  uid: UID;
  detailed: boolean;
  close: () => void;
}

export const isDevPanelMinimizedAtom = atom<boolean>(false);
export const devPanelActiveTabAtom = atom<string>("entities");

class CDevPanel extends World.Component("dev-panel")<
  CDevPanel,
  {
    isMinimizedAtom: PrimitiveAtom<boolean>;
    activeTabAtom: PrimitiveAtom<string>;
    heightAtom: PrimitiveAtom<number>;
  }
>() {}

class DevPanelEntity extends World.Entity("DevPanel", {
  components: [CDevPanel],
})<DevPanelEntity>() {}

interface DevPanelProps {
  trackedEntities: TrackedEntity[];
  onClearEntities: () => void;
}

export const DevPanel = ({ trackedEntities, onClearEntities }: DevPanelProps) => {
  const world = useWorldState();
  const uid = World.uid(null, null, "dev-panel");

  if (!world.getEntityMaybe(uid)) {
    world.addEntity(uid, DevPanelEntity, {
      "dev-panel": CDevPanel.of({
        isMinimizedAtom: isDevPanelMinimizedAtom,
        activeTabAtom: devPanelActiveTabAtom,
        heightAtom: atom(400),
      }),
    });
  }

  const entity = world.getEntity(uid, [CDevPanel]);
  const [isMinimized, setIsMinimized] = useAtom(entity["dev-panel"].isMinimizedAtom);
  const [activeTab, setActiveTab] = useAtom(entity["dev-panel"].activeTabAtom);
  const [height, setHeight] = useAtom(entity["dev-panel"].heightAtom);
  const isDraggingRef = useRef(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDraggingRef.current = true;
      const startY = e.clientY;
      const startHeight = height;

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDraggingRef.current) return;
        const delta = startY - e.clientY;
        const newHeight = Math.min(Math.max(startHeight + delta, 200), window.innerHeight * 0.8);
        setHeight(newHeight);
      };

      const handleMouseUp = () => {
        isDraggingRef.current = false;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [height, setHeight],
  );

  return (
    <div
      data-uid={uid}
      data-loc="phosphor-blog/scripts/world-state-demo/DevPanel.tsx:84:7" data-name="div" className="fixed bottom-0 right-0 backdrop-blur-sm w-[600px] max-w-[95vw] flex flex-col items-stretch z-50 transition-all duration-200 ease-in-out"
      style={{
        resize: "horizontal",
        overflow: isMinimized ? "hidden" : "auto",
        direction: "rtl",
        height: isMinimized ? "40px" : `${height}px`,
        backgroundColor: "#111111",
        color: "#f5f5f5",
        border: "1px solid #262626",
        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      }}
    >
      <div
        data-loc="phosphor-blog/scripts/world-state-demo/DevPanel.tsx:97:9" data-name="div" className="absolute top-0 left-0 w-full h-1 cursor-ns-resize transition-colors"
        onMouseDown={handleMouseDown}
        style={{ direction: "ltr", backgroundColor: "transparent" }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#262626")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
      />

      <div data-loc="phosphor-blog/scripts/world-state-demo/DevPanel.tsx:104:12" data-name="div" className="flex-1 h-[40px]">
        <div data-loc="phosphor-blog/scripts/world-state-demo/DevPanel.tsx:105:14" data-name="div" className="flex flex-col items-stretch h-full" style={{ direction: "ltr" }}>
          <div
            data-loc="phosphor-blog/scripts/world-state-demo/DevPanel.tsx:107:13" data-name="div" className="flex items-center overflow-hidden"
            style={{ borderBottom: "1px solid #262626", backgroundColor: "#0a0a0a" }}
          >
            <div
              data-loc="phosphor-blog/scripts/world-state-demo/DevPanel.tsx:111:15" data-name="div" className="flex items-center gap-1 p-2 select-none cursor-pointer transition-colors overflow-hidden"
              onClick={() => setIsMinimized((a) => !a)}
              style={{ backgroundColor: "transparent" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#262626")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <div data-loc="phosphor-blog/scripts/world-state-demo/DevPanel.tsx:117:20" data-name="div" className="text-sm shrink-0 truncate" style={{ fontWeight: 500, color: "#a3a3a3" }}>
                Dev Panel
              </div>
              <span data-loc="phosphor-blog/scripts/world-state-demo/DevPanel.tsx:120:21" data-name="span" style={{ color: "#737373" }}>
                {isMinimized ? "⬆" : "⬇"}
              </span>
            </div>
            <div data-loc="phosphor-blog/scripts/world-state-demo/DevPanel.tsx:124:18" data-name="div" className="h-full bg-transparent px-2 grow overflow-x-auto flex gap-2 items-center overflow-hidden">
              <button
                data-loc="phosphor-blog/scripts/world-state-demo/DevPanel.tsx:126:17" data-name="button" className="px-3 py-1 text-sm transition-colors"
                style={{
                  backgroundColor: activeTab === "entities" ? "#262626" : "transparent",
                  borderRadius: "4px",
                  fontWeight: activeTab === "entities" ? 600 : 400,
                  opacity: activeTab === "entities" ? 1 : 0.5,
                  color: "#f5f5f5",
                }}
                onClick={() => setActiveTab("entities")}
              >
                Entities {trackedEntities.length > 0 && `(${trackedEntities.length})`}
              </button>
            </div>
          </div>

          {!isMinimized && activeTab === "entities" && (
            <div data-loc="phosphor-blog/scripts/world-state-demo/DevPanel.tsx:142:18" data-name="div" className="overflow-x-auto flex gap-4 px-4 h-full items-stretch py-4">
              {trackedEntities.map(({ uid, detailed, close }) => (
                <div
                  data-loc="phosphor-blog/scripts/world-state-demo/DevPanel.tsx:145:19" data-name="div" className="min-w-[300px] min-h-10 resize-x overflow-auto relative shrink-0"
                  key={uid}
                  style={{ width: "300px" }}
                >
                  <DevEntityDetails data-loc="phosphor-blog/scripts/world-state-demo/DevPanel.tsx:149:37" data-name="DevEntityDetails" className="absolute inset-0 overflow-y-auto" uid={uid} detailed={detailed} onExit={close} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
